import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Divider, FormLabel, Grid, Stack, Typography, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import ReadOnlyField from '../../readOnlyField';

type Props = {
  questions: any;
  userResponses: Record<string, any>;
  maxHeight: number;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepReview = ({ questions = [], userResponses = {}, onBack, onCancel, onNext, maxHeight }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [actionPanelRef, actionPanelSize] = useElementSize();

  const handleNext = () => {
    onNext();
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  const isSectionArray = Array.isArray(questions);

  return (
    <Box
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {t('Review your responses')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Based on your responses, we will generate an event-driven AI pipeline. Review your configuration below before proceeding.
          </Typography>

          {/* DYNAMIC SECTION RENDERING (when questions is an array of sections) */}
          {isSectionArray ? (
            questions.map((section: any, idx: number) => (
              <Box key={section.id || idx} sx={{ mb: 2 }}>
                <Divider sx={{ mb: 1.5, mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, px: 1 }}>
                    {section.title || `Section ${idx + 1}`}
                  </Typography>
                </Divider>

                <Stack spacing={1.5}>
                  {(section.questions || []).map((q: any) => {
                    const rawVal = userResponses[q.id];
                    let displayVal = 'Not Answered';

                    if (rawVal !== undefined && rawVal !== null && rawVal !== '') {
                      if (Array.isArray(rawVal)) {
                        displayVal = rawVal
                          .map((val) => {
                            const opt = (q.options || []).find((o: any) => o.value === val);
                            return opt ? opt.label : val;
                          })
                          .join(', ');
                      } else if (typeof rawVal === 'boolean') {
                        displayVal = rawVal ? 'Yes (Enabled)' : 'No (Disabled)';
                      } else if (q.options && q.options.length > 0) {
                        const opt = q.options.find((o: any) => o.value === rawVal);
                        displayVal = opt ? opt.label : String(rawVal);
                      } else {
                        displayVal = String(rawVal);
                      }
                    }

                    return (
                      <ReadOnlyField
                        key={q.id}
                        label={q.questionText || q.id}
                        value={q.sensitive ? '********' : displayVal}
                        type={q.sensitive ? 'password' : 'text'}
                      />
                    );
                  })}
                </Stack>
              </Box>
            ))
          ) : (
            /* LEGACY OBJECT FALLBACK */
            <Stack spacing={2}>
              <Divider sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, px: 1 }}>
                  Requirements
                </Typography>
              </Divider>

              {questions?.mainRequirements && (
                <ReadOnlyField
                  label={questions.mainRequirements.question}
                  value={
                    questions.mainRequirements.options?.find((o: any) => o.value === userResponses.mainRequirements?.[0])
                      ?.label || 'Not Answered'
                  }
                />
              )}

              {questions?.documentProps && (
                <ReadOnlyField
                  label={questions.documentProps.question}
                  value={(userResponses.documentProps || []).map((op: string) => {
                    const option = questions.documentProps.options?.find((o: any) => o.value === op);
                    return option ? option.label : op;
                  })}
                />
              )}

              {questions?.dataSourceType && (
                <ReadOnlyField
                  label={questions.dataSourceType.question}
                  value={
                    questions.dataSourceType.options?.find((o: any) => o.value === userResponses.dataSourceType)
                      ?.label || 'Default'
                  }
                />
              )}
            </Stack>
          )}
        </Stack>
      </Box>

      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              Generate Pipeline
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepReview;
